import type { useSecureNoteForm } from '@frontend/forms';
import { getFileExceededError, getLocalFiles } from '@frontend/forms';
import { Fragment } from '@frontend/react';
import {
  Button,
  DragAndDropModal,
  FileInput,
  PasswordInput,
  TextInput,
  TextareaInput,
  XStack,
  YStack,
  showToast,
} from '@frontend/web-react';
import { PrivacyPolicyBanner } from '@frontend/web/components/DomainSpecific/PrivacyPolicyBanner';

type SecureNoteFormProps = {
  disableInputs: boolean;
  getControl: ReturnType<typeof useSecureNoteForm>['getControl'];
  hideSubmissionFooter: boolean;
  isNew: boolean;
  isReplaceSecureNoteContentLoading: boolean;
  isValid: boolean;
  setFieldValue: ReturnType<typeof useSecureNoteForm>['setFieldValue'];
  submitForm: ReturnType<typeof useSecureNoteForm>['submitForm'];
  values: ReturnType<typeof useSecureNoteForm>['values'];
};

export const SecureNoteForm = ({
  disableInputs,
  getControl,
  hideSubmissionFooter,
  isNew,
  isReplaceSecureNoteContentLoading,
  isValid,
  setFieldValue,
  submitForm,
  values,
}: SecureNoteFormProps) => {
  return (
    <YStack className="gap-y-md">
      <TextInput
        disabled={disableInputs}
        label="Email or username"
        placeholder="Email or username"
        value={getControl('username').value ?? ''}
        {...getControl('username', ['value'])}
      />
      <PasswordInput
        disabled={disableInputs}
        label="Password"
        value={getControl('password').value ?? ''}
        {...getControl('password', ['value'])}
      />
      <TextInput
        disabled={disableInputs}
        label="Website"
        placeholder="getduckbill.com"
        value={getControl('website').value ?? ''}
        {...getControl('website', ['value'])}
      />
      <FileInput
        allowedFileTypes={['image', 'pdf']}
        disabled={disableInputs}
        onChange={(files) => {
          const localFiles = getLocalFiles(files);
          const fileExceededError = getFileExceededError(localFiles);
          if (fileExceededError) {
            showToast({ title: fileExceededError, variant: 'danger' });
          } else {
            setFieldValue('attachments', files);
          }
        }}
        previewType="listItems"
        showPreview
        {...getControl('attachments', ['onChange'])}
      />
      <DragAndDropModal
        accept={{ IMAGE_MIME_TYPE: [] }}
        disabled={disableInputs}
        onFilesDropped={(attachments) => {
          const fileExceededError = getFileExceededError(attachments);
          if (fileExceededError) {
            showToast({ title: fileExceededError, variant: 'danger' });
          } else {
            setFieldValue('attachments', [...values.attachments, ...attachments]);
          }
        }}
      />
      <TextareaInput
        disabled={disableInputs}
        label="Notes"
        placeholder="Anything else we should know?"
        {...getControl('notes')}
        showOptionalText
      />

      {hideSubmissionFooter ? null : (
        <Fragment>
          <Button
            disabled={!isValid}
            isLoading={isReplaceSecureNoteContentLoading}
            label="Save"
            leftIconName="IconLock"
            onClick={submitForm}
            variant="primary"
          />
          {isNew ? (
            <XStack justifyContent="center">
              <PrivacyPolicyBanner />
            </XStack>
          ) : null}
        </Fragment>
      )}
    </YStack>
  );
};
